import React, { useEffect, useState, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTicketAlt, faReply, faCodeBranch, faUserPlus, faBookOpen } from '@fortawesome/free-solid-svg-icons';
import Swal from 'sweetalert2';
import InternalShell, { useInternalApi } from '../layouts/InternalShell';
import { SUPPORT_NAV, TICKET_STATUS_COLORS, PRIORITY_COLORS, SLA_META } from './nav';
import type { Ticket } from './LiveQueue';

interface TicketDetail extends Ticket {
    description: string;
    replies: { id: number; author_type: string; author_id: string; body: string; created: string }[];
}
interface Agent { id: number; full_name: string; department: string; }
interface Canned { id: number; title: string; category: string; body: string; use_count: number; }

const STATUSES = ['open', 'in-progress', 'resolved', 'closed'];
const PRIORITIES = ['low', 'medium', 'high', 'urgent'];

const TicketWorkbench: React.FC = () => {
    const { api } = useInternalApi();
    const [searchParams, setSearchParams] = useSearchParams();

    const [tickets, setTickets] = useState<Ticket[]>([]);
    const [agents, setAgents] = useState<Agent[]>([]);
    const [canned, setCanned] = useState<Canned[]>([]);
    const [loading, setLoading] = useState(true);
    const [filter, setFilter] = useState({ status: '', priority: '' });

    const [active, setActive] = useState<TicketDetail | null>(null);
    const [replyBody, setReplyBody] = useState('');
    const [sending, setSending] = useState(false);

    useEffect(() => { document.title = 'Ticket Workbench - Uzima Internal'; }, []);

    const loadTickets = useCallback(async () => {
        setLoading(true);
        try {
            const params = new URLSearchParams();
            if (filter.status) params.set('status', filter.status);
            if (filter.priority) params.set('priority', filter.priority);
            const res = await api.get(`admin-dashboard/support/tickets/?${params}`);
            if (res.data?.status === 'success') setTickets(res.data.data || []);
        } catch { setTickets([]); } finally { setLoading(false); }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [filter]);

    const openTicket = useCallback(async (id: number) => {
        try {
            const res = await api.get(`admin-dashboard/support/tickets/${id}/`);
            if (res.data?.status === 'success') {
                setActive(res.data.data);
                setSearchParams({ ticket: String(id) });
            }
        } catch {
            Swal.fire({ icon: 'error', title: 'Error', text: 'Could not load ticket.' });
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => { loadTickets(); }, [loadTickets]);

    useEffect(() => {
        api.get('admin-dashboard/support/agents/').then(r => { if (r.data?.status === 'success') setAgents(r.data.data || []); });
        api.get('admin-dashboard/support/knowledge-base/').then(r => { if (r.data?.status === 'success') setCanned(r.data.data.canned_responses || []); });
        const preselect = searchParams.get('ticket');
        if (preselect) openTicket(Number(preselect));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const patchTicket = async (payload: any) => {
        if (!active) return;
        try {
            const res = await api.patch(`admin-dashboard/support/tickets/${active.id}/`, payload);
            if (res.data?.status === 'success') {
                setActive(res.data.data);
                loadTickets();
            }
        } catch {
            Swal.fire({ icon: 'error', title: 'Error', text: 'Could not update ticket.' });
        }
    };

    const sendReply = async () => {
        if (!active || !replyBody.trim()) return;
        setSending(true);
        try {
            await api.post(`admin-dashboard/support/tickets/${active.id}/reply/`, { body: replyBody });
            setReplyBody('');
            openTicket(active.id);
            loadTickets();
        } catch {
            Swal.fire({ icon: 'error', title: 'Error', text: 'Could not post reply.' });
        } finally {
            setSending(false);
        }
    };

    const mergeTicket = async () => {
        if (!active) return;
        const others = tickets.filter(t => t.id !== active.id);
        if (others.length === 0) {
            Swal.fire({ icon: 'info', title: 'Nothing to merge into', text: 'There are no other tickets available.' });
            return;
        }
        const { value: target } = await Swal.fire({
            title: `Merge ticket #${active.id} into…`,
            input: 'select',
            inputOptions: others.reduce((acc, t) => ({ ...acc, [t.id]: `#${t.id} — ${t.subject}` }), {}),
            showCancelButton: true,
            inputPlaceholder: 'Select target ticket',
        });
        if (!target) return;
        try {
            await api.post(`admin-dashboard/support/tickets/${active.id}/merge/`, { into_ticket_id: Number(target) });
            Swal.fire({ icon: 'success', title: 'Merged', timer: 1200, showConfirmButton: false });
            setActive(null);
            loadTickets();
        } catch (err: any) {
            Swal.fire({ icon: 'error', title: 'Error', text: err?.response?.data?.message || 'Could not merge.' });
        }
    };

    return (
        <InternalShell activeDashboard="support_center" subNav={SUPPORT_NAV}>
            <h2 className="fw-bold mb-1"><FontAwesomeIcon icon={faTicketAlt} className="me-2 text-primary" />Ticket Workbench</h2>
            <p className="text-muted mb-4">Work a ticket end to end — assign, reply, escalate, merge</p>

            <div className="row">
                <div className="col-lg-5 mb-4">
                    <div className="card border-0 shadow-sm">
                        <div className="card-header bg-white d-flex gap-2">
                            <select className="form-select form-select-sm" value={filter.status} onChange={(e) => setFilter(f => ({ ...f, status: e.target.value }))}>
                                <option value="">All statuses</option>
                                {STATUSES.map(s => <option key={s} value={s}>{s}</option>)}
                            </select>
                            <select className="form-select form-select-sm" value={filter.priority} onChange={(e) => setFilter(f => ({ ...f, priority: e.target.value }))}>
                                <option value="">All priorities</option>
                                {PRIORITIES.map(p => <option key={p} value={p}>{p}</option>)}
                            </select>
                        </div>
                        <div className="card-body p-0" style={{ maxHeight: 620, overflowY: 'auto' }}>
                            {loading ? (
                                <div className="text-center py-4"><div className="spinner-border spinner-border-sm text-primary" role="status"><span className="visually-hidden">Loading...</span></div></div>
                            ) : tickets.length === 0 ? (
                                <div className="text-center py-4 text-muted">No tickets match.</div>
                            ) : (
                                <div className="list-group list-group-flush">
                                    {tickets.map(t => (
                                        <button key={t.id} className={`list-group-item list-group-item-action ${active?.id === t.id ? 'active' : ''}`} onClick={() => openTicket(t.id)}>
                                            <div className="d-flex justify-content-between">
                                                <strong className="small">#{t.id} {t.subject}</strong>
                                                <span className={`badge bg-${PRIORITY_COLORS[t.priority] || 'secondary'}`}>{t.priority}</span>
                                            </div>
                                            <div className={`small ${active?.id === t.id ? '' : 'text-muted'}`}>
                                                {t.clinic || '—'} · {t.status} · {t.assigned_to || 'unassigned'}
                                            </div>
                                        </button>
                                    ))}
                                </div>
                            )}
                        </div>
                    </div>
                </div>

                <div className="col-lg-7 mb-4">
                    {!active ? (
                        <div className="card border-0 shadow-sm"><div className="card-body text-center py-5 text-muted">
                            Select a ticket from the list to work it.
                        </div></div>
                    ) : (
                        <div className="card border-0 shadow-sm">
                            <div className="card-header bg-white">
                                <div className="d-flex justify-content-between align-items-start">
                                    <div>
                                        <h5 className="mb-1">#{active.id} {active.subject}</h5>
                                        <div className="small text-muted">
                                            {active.clinic || '—'} · filed by {active.submitted_by || 'unknown'} · {new Date(active.created).toLocaleString()}
                                        </div>
                                    </div>
                                    <span className={`badge bg-${SLA_META[active.sla_risk]?.color}`}>{SLA_META[active.sla_risk]?.label}</span>
                                </div>
                            </div>
                            <div className="card-body">
                                <div className="row g-2 mb-3">
                                    <div className="col-md-4">
                                        <label className="form-label small">Status</label>
                                        <select className="form-select form-select-sm" value={active.status} onChange={(e) => patchTicket({ status: e.target.value })}>
                                            {STATUSES.map(s => <option key={s} value={s}>{s}</option>)}
                                        </select>
                                    </div>
                                    <div className="col-md-4">
                                        <label className="form-label small">Priority</label>
                                        <select className="form-select form-select-sm" value={active.priority} onChange={(e) => patchTicket({ priority: e.target.value })}>
                                            {PRIORITIES.map(p => <option key={p} value={p}>{p}</option>)}
                                        </select>
                                    </div>
                                    <div className="col-md-4">
                                        <label className="form-label small"><FontAwesomeIcon icon={faUserPlus} className="me-1" />Assigned to</label>
                                        <select className="form-select form-select-sm" value={active.assigned_to_id || ''} onChange={(e) => patchTicket({ assigned_to_id: e.target.value || null })}>
                                            <option value="">Unassigned</option>
                                            {agents.map(a => <option key={a.id} value={a.id}>{a.full_name}</option>)}
                                        </select>
                                    </div>
                                </div>

                                <div className="p-3 bg-light rounded mb-3">
                                    <div className="small text-muted mb-1">Original report</div>
                                    {active.description}
                                </div>

                                <h6 className="fw-bold">Conversation</h6>
                                {active.replies.length === 0 ? (
                                    <p className="text-muted small">No replies yet.</p>
                                ) : (
                                    <div className="mb-3">
                                        {active.replies.map(r => (
                                            <div key={r.id} className={`p-2 mb-2 rounded ${r.author_type === 'internal_user' ? 'bg-primary bg-opacity-10' : 'bg-light'}`}>
                                                <div className="small text-muted mb-1">
                                                    {r.author_type === 'internal_user' ? 'Support agent' : 'Clinic staff'} · {new Date(r.created).toLocaleString()}
                                                </div>
                                                {r.body}
                                            </div>
                                        ))}
                                    </div>
                                )}

                                {canned.length > 0 && (
                                    <div className="mb-2">
                                        <label className="form-label small"><FontAwesomeIcon icon={faBookOpen} className="me-1" />Insert canned response</label>
                                        <select className="form-select form-select-sm" value="" onChange={(e) => {
                                            const c = canned.find(x => String(x.id) === e.target.value);
                                            if (c) setReplyBody(b => (b ? `${b}\n\n${c.body}` : c.body));
                                        }}>
                                            <option value="">Select a template…</option>
                                            {canned.map(c => <option key={c.id} value={c.id}>{c.title}</option>)}
                                        </select>
                                    </div>
                                )}

                                <textarea className="form-control mb-2" rows={3} placeholder="Type your reply…" value={replyBody} onChange={(e) => setReplyBody(e.target.value)} />
                                <div className="d-flex gap-2">
                                    <button className="btn btn-primary" disabled={sending || !replyBody.trim()} onClick={sendReply}>
                                        <FontAwesomeIcon icon={faReply} className="me-2" />{sending ? 'Sending…' : 'Send Reply'}
                                    </button>
                                    <button className="btn btn-outline-secondary" onClick={mergeTicket}>
                                        <FontAwesomeIcon icon={faCodeBranch} className="me-2" />Merge
                                    </button>
                                </div>
                            </div>
                        </div>
                    )}
                </div>
            </div>
        </InternalShell>
    );
};

export default TicketWorkbench;
